import { useState, useRef, useEffect } from "react";
import { useCategories } from "@/hooks/useCategories";
import { usePersona } from "@/contexts/PersonaContext";
import { api, apiPost } from "@/utils/api";
import type { NlpResult, ParsedMessage, AIStatus } from "@/types";

// ═══ Types ═══

export interface User {
  id: string;
  displayName: string;
  mail?: string;
  jobTitle?: string;
  department?: string;
}

// ═══ Custom Hook ═══

export function useChatConversation(
  users: User[],
  onAction: (result: NlpResult) => void
) {
  const { persona } = usePersona();
  const categories = useCategories();

  // State variables
  const [msgs, setMsgs] = useState<ParsedMessage[]>([
    {
      role: "bot",
      text: "Hey! I'm Synqus AI — your scheduling brain. Try things like:",
      aiCard: "quick_replies",
      aiData: [
        "Find me a time tomorrow afternoon with Sarah",
        "Book a standup next Tuesday, 15 min",
        "When can we meet between 10 and 2?",
        "Prep me for my 1:1",
      ],
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [pendingResult, setPendingResult] = useState<NlpResult | null>(null);
  const [peopleSearch, setPeopleSearch] = useState("");
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ role: string; content: string }>
  >([]);
  const [aiStatus, setAiStatus] = useState<AIStatus | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [configuring, setConfiguring] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [msgs]);

  // Check AI status on mount
  useEffect(() => {
    api<{ success: boolean; data: AIStatus }>("/api/ai/status")
      .then((res) => setAiStatus(res.data))
      .catch(() =>
        setAiStatus({
          enabled: false,
          model: "local",
          features: {
            smartParsing: false,
            timeSuggestions: true,
            meetingPrep: true,
            conversational: false,
          },
        })
      );
  }, []);

  // ═══ Conversation Flow Functions ═══

  const askNextQuestion = (result: NlpResult): void => {
    if (result.ambiguous && result.ambiguous.length > 0) {
      result.action = "disambiguate";
      setMsgs((p) => [
        ...p,
        {
          role: "bot",
          text: `I found ${result.ambiguous![0].matches.length} people matching "${result.ambiguous![0].term}". Which one?`,
          parsed: result,
        },
      ]);
      setPendingResult(result);
    } else if (!result._confirmedCategory) {
      result.action = "pick_category";
      const hint = result.category
        ? ` I'm guessing it's a ${
            categories.find((c2) => c2.id === result.category)?.label ||
            result.category
          } — is that right?`
        : "";
      setMsgs((p) => [
        ...p,
        {
          role: "bot",
          text: `What type of event is this?${hint}`,
          parsed: result,
        },
      ]);
      setPendingResult(result);
    } else if (
      !result.subcategory &&
      result.category &&
      categories.find((c2) => c2.id === result.category)?.subcategories?.length!
    ) {
      result.action = "pick_subcategory";
      const cat = categories.find((c2) => c2.id === result.category);
      setMsgs((p) => [
        ...p,
        {
          role: "bot",
          text: `What kind of ${cat?.label.toLowerCase()}?`,
          parsed: result,
        },
      ]);
      setPendingResult(result);
    } else if (!result.duration) {
      result.action = "pick_duration";
      const cat = categories.find((c2) => c2.id === result.category);
      const sub = cat?.subcategories?.find((s) => s.id === result.subcategory);
      const defaultDur =
        sub?.defaultDur || cat?.subcategories?.[0]?.defaultDur || 30;
      setMsgs((p) => [
        ...p,
        {
          role: "bot",
          text: "How long should it be?",
          parsed: { ...result, suggestedDur: defaultDur },
        },
      ]);
      setPendingResult(result);
    } else if (!result._askedAddPeople) {
      result._askedAddPeople = true;
      result.action = "add_people";
      const cat = categories.find((c2) => c2.id === result.category);
      const sub = cat?.subcategories?.find((s) => s.id === result.subcategory);
      const suggestedIds = (sub?.ids || []).filter(
        (id) => !result.people.includes(id)
      );
      const suggested = users.filter((u) => suggestedIds.includes(u.id));
      const currentNames = new Set(result.people);
      const otherSuggestions = users
        .filter(
          (u) =>
            !currentNames.has(u.id) && !suggestedIds.includes(u.id)
        )
        .slice(0, Math.max(0, 6 - suggested.length));
      result._suggestions = [...suggested, ...otherSuggestions].slice(0, 6);
      const currentPeopleNames = result.people
        .map((id) => {
          const u = users.find((u2) => u2.id === id);
          return u ? u.displayName.split(" ")[0] : id;
        })
        .join(", ");
      const typeLabel = sub?.label || cat?.label || "meeting";
      setMsgs((p) => [
        ...p,
        {
          role: "bot",
          text: `Got it — ${typeLabel} with ${currentPeopleNames}, ${result.duration} min. Want to add anyone else?`,
          parsed: result,
        },
      ]);
      setPendingResult(result);
    } else {
      result.action = "schedule";
      result.confidence = Math.max(result.confidence || 0, 65);
      const names = result.people.map((id) => {
        const u = users.find((u2) => u2.id === id);
        return u ? u.displayName.split(" ")[0] : id;
      });
      const parts: string[] = [];
      if (result.subcategory || result.category)
        parts.push((result.subcategory || result.category) || "");
      if (names.length > 0) parts.push(`with ${names.join(", ")}`);
      if (result.duration) parts.push(`${result.duration} min`);
      result.reply = `Ready to set up ${parts.join(", ")}. Look good?`;
      setMsgs((p) => [
        ...p,
        { role: "bot", text: result.reply!, parsed: result },
      ]);
      setPendingResult(result);
    }
  };

  const pickPerson = (
    ambigIndex: number,
    person: { id: string; displayName: string }
  ): void => {
    if (!pendingResult) return;
    const updated = { ...pendingResult };
    updated.people = [...updated.people, person.id];
    updated.ambiguous = updated.ambiguous?.filter((_, i) => i !== ambigIndex);
    setMsgs((p) => [...p, { role: "user", text: person.displayName }]);
    askNextQuestion(updated);
  };

  const pickCategory = (catId: string): void => {
    if (!pendingResult) return;
    const updated = {
      ...pendingResult,
      category: catId,
      _confirmedCategory: true,
    };
    if (pendingResult.category && pendingResult.category !== catId)
      updated.subcategory = undefined;
    const cat = categories.find((c2) => c2.id === catId);
    setMsgs((p) => [...p, { role: "user", text: cat?.label || catId }]);
    askNextQuestion(updated);
  };

  const pickSubcategory = (subId: string): void => {
    if (!pendingResult) return;
    const cat = categories.find((c2) => c2.id === pendingResult.category);
    const sub = cat?.subcategories?.find((s) => s.id === subId);
    const updated = { ...pendingResult, subcategory: subId };
    if (!updated.duration && sub?.defaultDur)
      updated.duration = sub.defaultDur;
    setMsgs((p) => [...p, { role: "user", text: sub?.label || subId }]);
    askNextQuestion(updated);
  };

  const pickDuration = (mins: number): void => {
    if (!pendingResult) return;
    const updated = { ...pendingResult, duration: mins };
    setMsgs((p) => [...p, { role: "user", text: `${mins} min` }]);
    askNextQuestion(updated);
  };

  const addPerson = (user: User): void => {
    if (!pendingResult) return;
    const updated = {
      ...pendingResult,
      people: [...pendingResult.people, user.id],
    };
    updated.action = "add_people";
    setPeopleSearch("");
    const names = updated.people
      .map((id) => {
        const u2 = users.find((u3) => u3.id === id);
        return u2 ? u2.displayName.split(" ")[0] : id;
      })
      .join(", ");
    setMsgs((p) => {
      const copy = [...p];
      for (let i = copy.length - 1; i >= 0; i--) {
        if (copy[i].role === "bot" && copy[i].parsed?.action === "add_people") {
          copy[i] = {
            ...copy[i],
            text: `Panel: ${names}. Search to add more or continue.`,
            parsed: updated,
          };
          break;
        }
      }
      return copy;
    });
    setPendingResult(updated);
  };

  const skipAddPeople = (): void => {
    if (!pendingResult) return;
    setPeopleSearch("");
    const names = pendingResult.people
      .map((id) => {
        const u = users.find((u2) => u2.id === id);
        return u ? u.displayName.split(" ")[0] : id;
      })
      .join(", ");
    setMsgs((p) => [
      ...p,
      { role: "user", text: `That's everyone: ${names}` },
    ]);
    askNextQuestion(pendingResult);
  };

  const confirmSchedule = (): void => {
    if (!pendingResult) return;
    setMsgs((p) => [
      ...p,
      { role: "user", text: "Yes, find slots!" },
      {
        role: "bot",
        text: "Finding available slots now...",
        parsed: { ...pendingResult, confirmed: true },
      },
    ]);
    onAction(pendingResult);

    // MOAT #3: Record booking signal for preference learning
    recordLearningSignal("booking", {
      duration: pendingResult.duration,
      category: pendingResult.category,
      subcategory: pendingResult.subcategory,
      people: pendingResult.people,
      platform: pendingResult.platform,
      startTime: new Date().toISOString(), // Will be updated when actual slot is picked
    });

    // Record duration choice if user explicitly set it
    if (pendingResult.duration) {
      recordLearningSignal("duration_choice", {
        duration: pendingResult.duration,
        category: pendingResult.category,
        wasDefault: false,
      });
    }

    setPendingResult(null);
    setTimeout(() => {}, 800);
  };

  const editSchedule = (): void => {
    setPendingResult(null);
    setMsgs((p) => [
      ...p,
      { role: "user", text: "Let me adjust" },
      {
        role: "bot",
        text: 'No problem — tell me what to change. e.g. "add Sarah" or "make it 60 min" or "change to interview"',
      },
    ]);
  };

  // ═══ MOAT #3: Learning Signal Helper ═══

  const recordLearningSignal = async (signalType: string, data: Record<string, unknown>): Promise<void> => {
    try {
      await apiPost("/api/ai/record-signal", { signalType, data });
    } catch {
      // Non-critical — never block user flow
    }
  };

  // ═══ AI-Enhanced Send Function ═══

  const send = async (overrideText?: string): Promise<void> => {
    const userMsg = (overrideText || input).trim();
    if (!userMsg || loading) return;
    if (!overrideText) setInput("");
    setMsgs((p) => [...p, { role: "user", text: userMsg }]);
    setLoading(true);
    setLoadingText(aiStatus?.enabled ? "AI is thinking..." : "Processing...");

    // Update conversation history for AI context
    const newHistory = [...conversationHistory, { role: "user", content: userMsg }];

    try {
      const minUsers = users.map((u) => ({
        id: u.id,
        displayName: u.displayName,
        jobTitle: u.jobTitle,
      }));
      const personaData = persona
        ? {
            id: persona.id,
            label: persona.label,
            terminology: persona.terminology,
            categories: persona.categories?.map((c2) => ({
              id: c2.id,
              label: c2.label,
            })),
          }
        : null;

      // Use AI endpoint (it falls back to regex internally)
      const res = await apiPost<NlpResult>("/api/ai/chat", {
        message: userMsg,
        users: minUsers,
        history: newHistory.slice(-8),
        persona: personaData,
      });

      // Update history with bot response
      setConversationHistory([
        ...newHistory,
        { role: "assistant", content: res.reply || "" },
      ]);

      // Handle different action types
      if (res.action === "chat") {
        // Conversational response from AI
        setMsgs((p) => [
          ...p,
          {
            role: "bot",
            text: res.reply || "I'm not sure what you mean.",
            aiCard:
              res.suggestions && res.suggestions.length > 0
                ? "quick_replies"
                : undefined,
            aiData: res.suggestions,
          },
        ]);
      } else if (res.action === "suggest_times") {
        // Time suggestion mode — enhanced with MOAT #2 NL Time-Finder
        setLoadingText("Checking calendars...");
        try {
          const timeRes = await apiPost<{ suggestions: unknown[]; learnedContext?: unknown; timeResolved?: unknown }>(
            "/api/ai/suggest-times",
            {
              people: res.people || [],
              events: [],
              duration: res.duration || 30,
              preferences: { timeHint: res.timeHint },
              timeConstraints: (res as any).timeResolved || null,  // MOAT #2
              timeHintText: userMsg, // Pass raw text for server-side NL resolution
            }
          );

          // Build enriched reply with time context
          let replyText = res.reply || "Here are some great times:";
          if (timeRes.learnedContext && (timeRes.learnedContext as any).confidence > 50) {
            replyText += " (tuned to your scheduling patterns)";
          }

          setMsgs((p) => [
            ...p,
            {
              role: "bot",
              text: replyText,
              aiCard: "time_suggestions",
              aiData: timeRes.suggestions || [],
            },
          ]);
        } catch {
          setMsgs((p) => [
            ...p,
            {
              role: "bot",
              text: res.reply || "I couldn't check availability right now.",
            },
          ]);
        }
      } else if (res.action === "meeting_prep") {
        // Meeting prep mode
        setLoadingText("Preparing your brief...");
        setMsgs((p) => [
          ...p,
          {
            role: "bot",
            text: res.reply || "Here's your meeting prep:",
            aiCard: "meeting_prep",
            aiData: res.prep,
          },
        ]);
      } else if (
        res.action === "disambiguate" ||
        (res.action === "schedule" && (res.confidence || 0) >= 20)
      ) {
        // Scheduling flow
        setMsgs((p) => [
          ...p,
          { role: "bot", text: res.reply || "", parsed: res },
        ]);

        if (res.source === "ai" && res.confidence && res.confidence >= 70) {
          // AI is confident — auto-advance through guided flow
          const aiResult: NlpResult = {
            ...res,
            people: res.people || [],
            _confirmedCategory: !!res.category,
          };
          if (
            res.action === "disambiguate" &&
            res.ambiguous &&
            res.ambiguous.length > 0
          ) {
            setPendingResult(aiResult);
          } else {
            askNextQuestion(aiResult);
          }
        } else {
          // Low confidence or local NLP — use guided flow
          if (
            res.action === "disambiguate" &&
            res.ambiguous &&
            res.ambiguous.length > 0
          ) {
            setPendingResult(res);
          } else {
            askNextQuestion(res);
          }
        }
      } else {
        setMsgs((p) => [
          ...p,
          { role: "bot", text: res.reply || "I didn't understand. Try again?" },
        ]);
      }
    } catch {
      setMsgs((p) => [
        ...p,
        { role: "bot", text: "Sorry, something went wrong. Try again?" },
      ]);
    }
    setLoading(false);
    setLoadingText("");
  };

  // ═══ API Key Configuration ═══

  const configureAI = async (): Promise<void> => {
    if (!apiKeyInput.trim()) return;
    setConfiguring(true);
    try {
      await apiPost("/api/ai/configure", { apiKey: apiKeyInput.trim() });
      setAiStatus((prev) =>
        prev
          ? {
              ...prev,
              enabled: true,
              features: {
                ...prev.features,
                smartParsing: true,
                conversational: true,
              },
            }
          : null
      );
      setShowSettings(false);
      setApiKeyInput("");
      setMsgs((p) => [
        ...p,
        {
          role: "bot",
          text: "AI features activated! I'm now powered by GPT and can understand natural language much better. Try asking me something complex!",
        },
      ]);
    } catch {
      setMsgs((p) => [
        ...p,
        {
          role: "bot",
          text: "Couldn't validate that API key. Double-check it and try again.",
        },
      ]);
    }
    setConfiguring(false);
  };

  return {
    // State
    msgs,
    input,
    setInput,
    loading,
    loadingText,
    pendingResult,
    aiStatus,
    showSettings,
    setShowSettings,
    apiKeyInput,
    setApiKeyInput,
    configuring,
    scrollRef,
    peopleSearch,
    setPeopleSearch,

    // Actions
    send,
    pickPerson,
    pickCategory,
    pickSubcategory,
    pickDuration,
    addPerson,
    skipAddPeople,
    confirmSchedule,
    editSchedule,
    configureAI,
    recordLearningSignal,
    addMessage: (msg: ParsedMessage) => setMsgs((p) => [...p, msg]),
  };
}
